import React, { useEffect, useState } from "react";
import { Outlet, Link, useNavigate, useLocation, useOutletContext, Navigate } from "react-router-dom";
import { ChevronDown, Search, Clock, Plus } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../../components/ui/dropdown-menu";
import { ujmsAccess } from "../../services/ujmsApi";
import { useAuth } from "../../context/AuthContext";
import { NotificationBell } from "../NotificationBell";
import { PdfViewer } from "../PdfViewer";
import { BrandMark, useBranding } from "../branding";
import { PdfButton } from "../PdfButton";
import { cls, can } from "../ui";
import { StatusBar } from "./StatusBar";
import { ProfileMenu } from "./ProfileMenu";
import { EDITIONS, editionsFor, homeRoute, partnerEdition } from "./editions";
import { CourtWayfinding, routeLabel } from "./workspace/CourtWayfinding";
import { useWorkingCase } from "./workspace/useWorkingCase";
import { ApplicationTabs } from "./ApplicationTabs";

/** /start — sends a freshly signed-in user straight to their own edition once permissions are known. */
export function RoleHome() {
  const { access } = useOutletContext();
  const next = new URLSearchParams(useLocation().search).get("next");
  if (next && (next === "/portal" || editionsFor(access).some((e) => e.route === next))) {
    return <Navigate to={next} replace />;
  }
  return <Navigate to={homeRoute(access)} replace />;
}

const ADD = { name: "Add", items: [{ to: "/ujms/intake", label: "New case", perm: ["case.create"] }, { to: "/officer?view=arrests", label: "Arrest report / booking", perm: ["criminal.refer"] }, { to: "/ujms/arrests", label: "Arrest review / case entry", perm: ["case.create", "charge.manage"] }, { to: "/ujms/records", label: "Person", perm: ["person.view", "case.view"] }, { to: "/ujms/filings", label: "Filing", perm: ["filing.accept"] }, { to: "/ujms/efile", label: "eFile submission", perm: ["efile.submit", "filing.accept"] }, { to: "/ujms/calendar", label: "Hearing", perm: ["hearing.schedule", "hearing.manage"] }, { to: "/ujms/warrants", label: "Warrant", perm: ["warrant.request", "warrant.issue"] }, { to: "/ujms/sheriff", label: "Bond / custody", perm: ["custody.manage"] }, { to: "/ujms/tasks", label: "Task", perm: ["task.view"] }, { to: "/ujms/tills", label: "Payment", perm: ["finance.manage"] }] };

const SUPPORT = "mailto:melgiribosso@gmail.com";
/** Trial workspaces carry their commercial state at the top of the working area — days left, contract pending/approved, or purchased. */
function TrialBanner({ tenant }) {
  if (!tenant || tenant.status === "ACTIVE") return null;
  const days = tenant.days_left ?? 0;
  const trial = tenant.status === "TRIAL";
  const tone = trial ? (days <= 5 ? "bg-red-50 border-red-300 text-red-900" : "bg-amber-50 border-amber-300 text-amber-900") : "bg-sky-50 border-sky-300 text-sky-900";
  const subject = encodeURIComponent(`CJMS — ${tenant.court_name}: ${trial ? "purchase / contract" : "contract status"}`);
  return (
    <div className={cls("flex items-center gap-2 border px-2 py-[3px] mb-1.5 text-[11.5px] rounded-sm", tone)} data-testid="trial-banner" data-status={tenant.status}>
      <Clock className="w-3.5 h-3.5 shrink-0" />
      <span className="font-semibold" data-testid="trial-banner-text">
        {trial ? <>Trial workspace · <b>{days}</b> day{days === 1 ? "" : "s"} left (ends {String(tenant.trial_expires_at || "").slice(0, 10)})</> : tenant.status === "PENDING_CONTRACT" ? "Contract pending — your workspace stays active while RB Technologies finalizes it" : "Contract approved — your workspace stays active"}
      </span>
      <span className="truncate hidden md:inline">{trial ? "— unless purchased or a contract is pending or approved, the workspace terminates on day 30." : ""}</span>
      <a href={`${SUPPORT}?subject=${subject}`} className="ml-auto underline font-semibold whitespace-nowrap" data-testid="trial-banner-contact">{trial ? "Purchase or start a contract" : "Contact RB Technologies"}</a>
    </div>
  );
}

/** One frame for every role: command bar · explorer tree · working area · status bar. */
export default function EnterpriseShell() {
  const brand = useBranding();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [access, setAccess] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { setQ(new URLSearchParams(loc.search).get("q") || ""); }, [loc.pathname, loc.search]);
  useEffect(() => { ujmsAccess().then((r) => setAccess(r.data)).catch(() => setAccess({ permissions: [], ujms_roles: [] })); }, []);
  const focusWorkspace = /^\/documents\/[^/]+\/view|^\/(clerk|judge)\/session\//.test(loc.pathname);
  const working = useWorkingCase(access, loc.pathname);
  const additions = access && !partnerEdition(access) ? ADD.items.filter((it) => can(access, ...it.perm)) : [];
  const menus = additions.length ? [{ key: "add", items: additions }] : [];
  const edition = EDITIONS.find((e) => loc.pathname === e.route || loc.pathname.startsWith(e.route + "/"));
  useEffect(() => { document.title = `${brand.court_name} · ${routeLabel(loc.pathname)} · CJMS`; }, [brand.court_name, loc.pathname]);
  return (
    <div className="cjms-shell h-screen flex flex-col text-slate-900 overflow-hidden" data-testid="enterprise-shell">
      <a href="#ujms-main" className="cjms-skip" data-testid="cjms-skip-content">Skip to content</a>
      <header className="ent-nav cjms-topbar text-white flex items-center shrink-0 no-print" data-testid="ent-topnav">
        <Link to={homeRoute(access)} className="court-institution-brand" data-testid="ent-brand" title={`${brand.court_name} — home`}>
          <BrandMark className="w-8 h-8" testId="court-header-seal" /><span><strong data-testid="cjms-court-name">{brand.court_name}</strong><small data-testid="court-powered-by">Powered by CJMS</small></span>
        </Link>
        <form className="cjms-top-search" onSubmit={(e) => { e.preventDefault(); const next = loc.pathname === "/search" ? new URLSearchParams(loc.search) : new URLSearchParams({ scope: "cases" }); next.set("q", q.trim()); nav(`/search?${next}`); }} data-testid="ent-global-search-form">
          <Search size={15} /><input aria-label="Search permitted records" placeholder={partnerEdition(access) ? "Search this partner’s records…" : "Search cases, documents, people…"} value={q} onChange={(e) => setQ(e.target.value)} data-testid="ent-global-search" />
          <button type="submit" aria-label="Run global search" data-testid="cjms-global-search-submit"><ChevronDown className="-rotate-90" size={14} /></button>
        </form>
        {menus.map((m) => (
          <DropdownMenu key={m.key}>
            <DropdownMenuTrigger asChild>
              <button className="cjms-new-button" data-testid={`ent-nav-${m.key}`}><Plus size={14} /><span>New</span> <ChevronDown className="w-3 h-3 opacity-70" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[210px] rounded-none text-[12px] p-0.5 max-h-[80vh] overflow-auto" data-testid={`ent-menu-${m.key}`}>
              {m.items.map((it, i) => it.sep ? <DropdownMenuSeparator key={i} /> : (
                <DropdownMenuItem key={it.label} onSelect={() => nav(it.to)} className="rounded-none py-1 cursor-pointer" data-testid={`ent-menu-item-${m.key}-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{it.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ))}
        <div className="cjms-header-print"><PdfButton testId="ent-pdf-btn" /></div>
        <NotificationBell />
        <ProfileMenu access={access} />
      </header>
      {access && <ApplicationTabs access={access} />}
      <div className="flex flex-1 min-h-0">
        <main className={cls("flex-1 min-w-0 flex flex-col court-shell-main", focusWorkspace && "court-focus-workspace")} id="ujms-main" data-testid="ent-main" tabIndex={-1}>
          {access && !/^\/(?:ujms\/)?cases\//.test(loc.pathname) && <CourtWayfinding access={access} {...working} />}
          <div className={cls("court-route-content", focusWorkspace ? "court-route-focus" : "cjms-main")} key={loc.pathname}>
          <TrialBanner tenant={user?.tenant} />
          {access ? <Outlet context={{ access, setWorkingCase: working.selectCase }} /> : <div className="text-xs text-slate-400 p-4" data-testid="court-workspace-loading">Loading workspace…</div>}
          </div>
        </main>
      </div>
      <StatusBar access={access} />
      <PdfViewer />
    </div>
  );
}
